import React, { useReducer, useState, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { arrayReducer, initStateArray } from 'reducers/default'
import { AssetClass, isCampaignPublished, tcDisplayName } from 'api/models'
import { fetchAudiences } from 'api/integrations'
import Field from './Field'
import SearchDropdownMultiSelect from '../SearchDropdownMultiSelect'
import { getErrorText } from '../adAccountHelper'

export default function AudienceField({ audienceId, campaign, onChange, event }) {
  const { t } = useTranslation()
  const { setup } = useCampaignSetup()
  const [customAudiences, dispatchCustomAudiencesAction] = useReducer(arrayReducer, initStateArray)
  const [savedAudiences, dispatchSavedAudiencesAction] = useReducer(arrayReducer, initStateArray)
  const [isAudiencesFetched, setIsAudiencesFetched] = useState(false)

  const isPublished = isCampaignPublished(campaign)

  const hasAudienceLimitations = useMemo(() => {
    if (!event || !event.special_ad_category) {
      return false
    }
    return (
      event.special_ad_category?.length > 0
    )
  }, [event])

  const retrieveAudiences = useCallback(() => {
    const adAccountId = (setup.integration_details || []).find(
      (x) => x.asset_class === AssetClass.ad_account,
    )?.asset_id

    if (!isAudiencesFetched && adAccountId) {
      fetchAudiences(dispatchCustomAudiencesAction, adAccountId, 'custom')
      fetchAudiences(dispatchSavedAudiencesAction, adAccountId, 'saved')
      setIsAudiencesFetched(true)
    }
  }, [setup.integration_details, isAudiencesFetched])

  useEffect(() => {
    retrieveAudiences()
  }, [retrieveAudiences])

  const handleAudienceChange = (audienceIdsString) => {
    if (!audienceIdsString) {
      onChange({ audience_id: '', saved_audience_id: '' })
      return
    }

    const audienceIds = audienceIdsString.split(',').filter(Boolean)
    const customAudienceIds = []
    const savedAudienceIds = []

    audienceIds.forEach((id) => {
      const isCustom = customAudiences.items?.some((audience) => audience.id === id)
      const isSaved = savedAudiences.items?.some((audience) => audience.id === id)

      if (isSaved) {
        savedAudienceIds.push(id)
      } else if (isCustom) {
        customAudienceIds.push(id)
      }
    })

    const audience_id = customAudienceIds.length > 0 ? customAudienceIds.join(',') : ''
    const saved_audience_id = savedAudienceIds.length > 0 ? savedAudienceIds[0] : ''

    onChange({ audience_id, saved_audience_id })
  }

  return (
    <Field
      fieldName="audience"
      as={SearchDropdownMultiSelect}
      tooltip={{ content: 'Tips.Recommendations.CreativeComposer.Audience' }}
      inputTooltip={
        {
          body: t('Tips.Recommendations.CreativeComposer.AudienceLimitations.body'),
          enabled: hasAudienceLimitations,
          withAnchor: false,
          placement: 'top',
        }
      }
      value={audienceId}
      defaultValue={t('Recommendations.campaign.recommendationTitle', {
        tc: tcDisplayName(campaign),
      })}
      onChange={handleAudienceChange}
      isRequired={false}
      disabled={isPublished || hasAudienceLimitations}
      options={customAudiences.items}
      savedAudiences={savedAudiences.items}
      error={{
        custom: customAudiences.error ? getErrorText(customAudiences.message) : null,
        saved: savedAudiences.error ? getErrorText(savedAudiences.message) : null}}
      loading={{
        custom: customAudiences.loading,
        saved: savedAudiences.loading}}
      onErrorBtnClick={retrieveAudiences}
      dropdownClasses="audience"
    />
  )
}

AudienceField.propTypes = {
  audienceId: PropTypes.string,
  campaign: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  event: PropTypes.object,
}
